/**
 * useBannedPlayers - Manages the per-league player ban list with DB persistence and localStorage fallback.
 */

import { API_ENDPOINTS, apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import {
  getBannedPlayersForLeague,
  normalizeBannedPlayers,
} from '@parlay-picker/shared/analysisConfig';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { safeParseJson } from '@utils/api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface AvailablePlayer {
  id?: string | number;
  name: string;
  teamAbbr?: string;
}

interface BanListSaveError {
  message: string;
  type: 'save_error';
}

export interface UseBannedPlayersResult {
  bannedPlayers: string[];
  availablePlayers: AvailablePlayer[];
  loadingPlayers: boolean;
  addToBanList: (playerName: string) => void;
  addPlayersToBanList: (playerNames: string[]) => void;
  removeFromBanList: (playerName: string) => void;
  removePlayersFromBanList: (playerNames: string[]) => void;
}

export function useBannedPlayers(
  leagueKey: LeagueKey,
  onSaveError: (error: BanListSaveError) => void,
): UseBannedPlayersResult {
  const { user, authFetch, updateBannedPlayers } = useAuth();

  // Derive league-specific ban list from user's per-league map
  const leagueBannedPlayers = useMemo(() => {
    if (!user?.bannedPlayers) return [];
    const map = normalizeBannedPlayers(user.bannedPlayers);
    return getBannedPlayersForLeague(map, leagueKey);
  }, [user?.bannedPlayers, leagueKey]);

  const [bannedPlayers, setBannedPlayers] = useState<string[]>(leagueBannedPlayers);
  const [availablePlayers, setAvailablePlayers] = useState<AvailablePlayer[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState<boolean>(false);

  // Sync when league or user data changes
  useEffect(() => {
    setBannedPlayers(leagueBannedPlayers);
  }, [leagueBannedPlayers]);

  // The route requires leagueKey and answers 400 without it.
  const fetchAllPlayers = useCallback(async (): Promise<void> => {
    setLoadingPlayers(true);
    try {
      const res = await authFetch(
        apiUrl(`${API_ENDPOINTS.PLAYERS_ALL}?leagueKey=${encodeURIComponent(leagueKey)}`),
      );
      const data = (await safeParseJson(res)) as {
        error?: string;
        players?: AvailablePlayer[];
      };
      if (data.error) throw new Error(data.error);
      setAvailablePlayers(data.players || []);
    } catch (_err) {
    } finally {
      setLoadingPlayers(false);
    }
  }, [authFetch, leagueKey]);

  useEffect(() => {
    fetchAllPlayers();
  }, [fetchAllPlayers]);

  const persistBanList = useCallback(
    async (newList: string[]): Promise<void> => {
      if (!user || !updateBannedPlayers) return;
      try {
        await updateBannedPlayers(newList, leagueKey);
      } catch {
        onSaveError({
          message: "Couldn't save your ban list. Try the change again.",
          type: 'save_error',
        });
      }
    },
    [user, updateBannedPlayers, leagueKey, onSaveError],
  );

  // Each change builds on the list the last one left, so changes made before a render all keep,
  // and the effect below saves the final list once.
  const hasUnsavedBans = useRef(false);

  const updateBanList = (update: (current: string[]) => string[]) => {
    hasUnsavedBans.current = true;
    setBannedPlayers(update);
  };

  useEffect(() => {
    if (!hasUnsavedBans.current) return;
    hasUnsavedBans.current = false;
    void persistBanList(bannedPlayers);
  }, [bannedPlayers, persistBanList]);

  const addPlayersToBanList = (playerNames: string[]): void => {
    if (playerNames.every((name) => bannedPlayers.includes(name))) return;
    updateBanList((current) => [
      ...current,
      ...playerNames.filter((name) => !current.includes(name)),
    ]);
  };

  const addToBanList = (playerName: string): void => addPlayersToBanList([playerName]);

  const removePlayersFromBanList = (playerNames: string[]): void => {
    const namesToRemove = new Set(playerNames);
    updateBanList((current) => current.filter((p) => !namesToRemove.has(p)));
  };

  const removeFromBanList = (playerName: string): void => removePlayersFromBanList([playerName]);

  return {
    bannedPlayers,
    availablePlayers,
    loadingPlayers,
    addToBanList,
    addPlayersToBanList,
    removeFromBanList,
    removePlayersFromBanList,
  };
}
